import React, { useCallback, useEffect, useState, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { ToastContainer } from 'react-toastify';

import { Form, Formik } from 'formik';

import { createLocale } from 'locales';

import type { AsyncSelectTagOption } from 'components-new/async-select-tags/async-select-tags';
import AsyncSelectTags from 'components-new/async-select-tags/async-select-tags';
import Button from 'components-new/button/button';
import Typography from 'components-new/typography/typography';
import { showErrorToast, showSuccessToast } from 'components-new/toast/toast';

import { STATIC_FILES_URL } from 'constants/signup-flow-constants';

import CheckboxField from 'form-elements/checkbox-field';

import type { UpdateFreelancerCompetencesType } from 'state/remote/remote-freelancer/use-update-freelancer-competencies';
import useUpdateFreelancerCompetencies from 'state/remote/remote-freelancer/use-update-freelancer-competencies';
import useGetCompetenceOptions from 'state/remote/remote-freelancer/use-get-competence-options';

import FreelancerSignUpPageContainer from '../components/freelancer-sign-up-page-container';

import dialogInstructionsContent from '../PersonalInformation/dialogInstructionsContent';
import freelancerRoutes from '../freelancer-routes';

import validationSchema from './schema';

const url = `${STATIC_FILES_URL}videos/signupflow/h2.mp4`;
const videoPreviewImageURL = `${window.STATIC_URL}assets/signupflow/h2.png`;

const localize = createLocale('freelancerCompetenciesInformationPageLocales');
const globalLocalize = createLocale('global');

const CompetenciesInformation: React.FC = () => {
  const navigate = useNavigate();
  const {
    initialValues,
    isSuccess,
    isLoading,
    error,
    updateFreelancerCompetencies,
  } = useUpdateFreelancerCompetencies();
  const { competenceOptions } = useGetCompetenceOptions();
  const [competenceQuery, setCompetenceQuery] = useState<string | null>(null);
  const [selectedCompetenceIds, setSelectedCompetenceIds] = useState<number[]>(
    [],
  );

  const competenceSuggestions = useMemo(() => {
    const suggestions: AsyncSelectTagOption[] = [];
    if (initialValues) {
      competenceOptions.forEach((choice, i) => {
        const competenceValue =
          initialValues.competences?.[choice.code] ?? false;

        suggestions.push({
          active: (competenceValue as boolean | string) === true,
          disabled: (competenceValue as boolean | string) === 'disabled',
          id: i,
          value: choice.name,
        });
      });
    }

    selectedCompetenceIds.forEach((competenceId) => {
      const sug = suggestions.find(
        (suggestion) => suggestion.id === competenceId,
      ) as AsyncSelectTagOption;
      sug.active = !sug.active;
    });

    if (competenceQuery)
      return suggestions.filter((suggestion) =>
        suggestion.value.toLowerCase().includes(competenceQuery.toLowerCase()),
      );
    return suggestions;
  }, [
    competenceOptions,
    competenceQuery,
    initialValues,
    selectedCompetenceIds,
  ]);

  const selectedCompetencesResult = useMemo(() => {
    const result: string[] = [];

    competenceSuggestions.forEach((suggestion) => {
      const competenceKey = (
        competenceOptions.find(
          (option) => option.name === suggestion.value,
        ) as {
          code: string;
          name: string;
        }
      ).code;
      if (suggestion.active) result.push(competenceKey);
    });

    const allCompetenceCodes = competenceOptions.map((option) => option.code);

    const myCompetences = {} as UpdateFreelancerCompetencesType['competences'];
    allCompetenceCodes.forEach((code) => {
      if (myCompetences) myCompetences[code] = result.includes(code);
    }, {} as UpdateFreelancerCompetencesType['competences']);
    return myCompetences;
  }, [competenceOptions, competenceSuggestions]);

  const navigateNext = useCallback(
    () =>
      navigate(
        `${freelancerRoutes.kvkInformation.path}?showVideoInstructions=true`,
      ),
    [navigate],
  );

  const navigateToPersonalInformationPage = useCallback(
    () => navigate(freelancerRoutes.personalInformation.path),
    [navigate],
  );

  const onSelectedCompetenceChange = useCallback((ids: Array<number>) => {
    setSelectedCompetenceIds(ids);
  }, []);

  const onUpdateCompetenciesClicked = useCallback(
    (values: UpdateFreelancerCompetencesType) => {
      if (
        selectedCompetencesResult &&
        !Object.values(selectedCompetencesResult).some(
          (value) => value === true,
        )
      ) {
        showErrorToast({
          description: localize('competencesRequired'),
          title: globalLocalize('error'),
        });
        return;
      }
      updateFreelancerCompetencies({
        ...values,
        competences: selectedCompetencesResult,
      });
    },
    [selectedCompetencesResult, updateFreelancerCompetencies],
  );

  useEffect(() => {
    if (isSuccess) {
      showSuccessToast({
        description: localize('CompetencesAdded'),
        title: globalLocalize('notification'),
      });
      navigateNext();
    }
    if (error) {
      showErrorToast({
        description: error?.message,
        title: globalLocalize('error'),
      });
    }
  }, [error, isSuccess, navigateNext]);

  return (
    <FreelancerSignUpPageContainer
      showHeader
      showGoBackButton
      onGoBackClick={navigateToPersonalInformationPage}
      headerTitle={localize('title')}
      videoInstructions={{
        url,
        videoPreviewImageURL,
      }}
      dialogInstructions={{
        component: dialogInstructionsContent,
      }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onUpdateCompetenciesClicked}
        enableReinitialize
      >
        <Form>
          <Typography variant="body-medium" as="p" className="my-4">
            {localize('needMoreInformation')}
          </Typography>
          <Typography variant="body-large-bold" as="p" className="my-2">
            {localize('competences')}
          </Typography>

          <AsyncSelectTags
            className="w-full mb-4"
            placeholder={localize('selectCompetences')}
            options={competenceSuggestions}
            query=""
            onQueryChange={(query) => setCompetenceQuery(query)}
            onSelectedValueChange={onSelectedCompetenceChange}
          />
          <Typography variant="body-large-bold" as="p" className="mb-2">
            {localize('licenses')}
          </Typography>
          <CheckboxField
            name="hasDriverLicense"
            labelComponent={
              <Typography variant="body-medium" as="span" className="ml-4">
                {localize('ownsDriverLicense')}
              </Typography>
            }
            className="my-3"
          />
          <CheckboxField
            name="hasStintLicense"
            labelComponent={
              <Typography variant="body-medium" as="span" className="ml-4">
                {localize('ownsStintLicense')}
              </Typography>
            }
            className="my-3"
          />
          <Button
            loading={isLoading}
            disabled={isLoading}
            label={globalLocalize('continue')}
            type="submit"
            variant="primary"
            className="my-3 w-full flex flex-row items-center justify-center"
          />
        </Form>
      </Formik>
      <ToastContainer />
    </FreelancerSignUpPageContainer>
  );
};

export default CompetenciesInformation;
